import { useMemo, useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  Archive,
  Bell,
  BellOff,
  CheckCheck,
  CornerDownLeft,
  Filter,
  Info,
  Power,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  SEVERITY_META,
  SOURCE_META,
  useNotifications,
  type AlertSeverity,
  type AlertSource,
  type AppAlert,
} from '@/store/notifications';
import { cn } from '@/lib/utils';

/**
 * Notifications center — bell button with unread dot for the TopBar, plus
 * a hover-friendly drawer with the alert list, filters, mute controls, and
 * mark-all-read. Backed by the local `useNotifications` store; once the
 * SQLite `audit_log` + `events` tables land, the same shape persists.
 */
export function NotificationsCenter() {
  const alerts = useNotifications((s) => s.alerts);
  const audio = useNotifications((s) => s.audio);
  const desktopPush = useNotifications((s) => s.desktopPush);
  const toggleAudio = useNotifications((s) => s.toggleAudio);
  const toggleDesktopPush = useNotifications((s) => s.toggleDesktopPush);
  const markAllRead = useNotifications((s) => s.markAllRead);
  const clearAll = useNotifications((s) => s.clearAll);
  const muted = useNotifications((s) => s.muted);
  const unmute = useNotifications((s) => s.unmute);

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<AlertSeverity | null>(null);

  const list = useMemo(() => {
    const all = Object.values(alerts).sort((a, b) => b.ts - a.ts);
    return filter ? all.filter((a) => a.severity === filter) : all;
  }, [alerts, filter]);

  const unreadCount = useMemo(
    () => Object.values(alerts).filter((a) => !a.read).length,
    [alerts],
  );
  const criticalUnread = useMemo(
    () => Object.values(alerts).filter((a) => !a.read && a.severity === 'critical').length,
    [alerts],
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`${unreadCount} unread alert${unreadCount === 1 ? '' : 's'}`}
        className={cn(
          'relative grid h-7 w-7 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary',
          criticalUnread > 0 && 'text-status-critical hover:text-status-critical',
        )}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 grid h-3.5 min-w-[14px] place-items-center rounded-full px-0.5 text-[8.5px] font-bold text-fg-primary',
              criticalUnread > 0 ? 'bg-status-critical' : 'bg-accent',
            )}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <aside
            role="dialog"
            aria-label="Notifications"
            className="absolute right-0 top-full z-50 mt-1 flex w-[380px] flex-col rounded-lg border border-border-default bg-surface-1 shadow-[var(--shadow-pop)]"
          >
            <header className="flex items-center gap-2 border-b border-border-default px-3 py-2">
              <Bell className="h-4 w-4 text-accent" />
              <h2 className="text-[12.5px] font-semibold text-fg-primary">Notifications</h2>
              {unreadCount > 0 && (
                <Badge tone={criticalUnread > 0 ? 'critical' : 'accent'}>
                  {unreadCount} unread
                </Badge>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-auto grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </header>

            <div className="flex items-center gap-1 border-b border-border-subtle px-3 py-1.5">
              <Filter className="h-3 w-3 text-fg-muted" />
              <SevPill active={filter === null} onClick={() => setFilter(null)}>All</SevPill>
              <SevPill active={filter === 'critical'} onClick={() => setFilter(filter === 'critical' ? null : 'critical')}>
                <AlertOctagon className="h-2.5 w-2.5 text-status-critical" /> Critical
              </SevPill>
              <SevPill active={filter === 'warn'} onClick={() => setFilter(filter === 'warn' ? null : 'warn')}>
                <AlertTriangle className="h-2.5 w-2.5 text-status-warn" /> Warn
              </SevPill>
              <SevPill active={filter === 'info'} onClick={() => setFilter(filter === 'info' ? null : 'info')}>
                <Info className="h-2.5 w-2.5 text-fg-muted" /> Info
              </SevPill>
            </div>

            {Object.keys(muted).length > 0 && (
              <div className="border-b border-border-subtle bg-amber-500/5 px-3 py-1.5 text-[10.5px] text-amber-300">
                <BellOff className="mr-1 inline h-3 w-3" />
                Muted:{' '}
                {(Object.entries(muted) as [AlertSource, number][])
                  .filter(([, expiry]) => expiry > Date.now())
                  .map(([source, expiry]) => (
                    <button
                      key={source}
                      type="button"
                      onClick={() => unmute(source)}
                      className="mx-1 inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[9.5px] hover:bg-amber-500/20"
                    >
                      {SOURCE_META[source].label} · {fmtRemain(expiry)} <X className="h-2.5 w-2.5" />
                    </button>
                  ))}
              </div>
            )}

            <ul className="max-h-[60vh] flex-1 divide-y divide-border-subtle overflow-y-auto">
              {list.length === 0 ? (
                <li className="px-3 py-8 text-center text-[12px] text-fg-muted">
                  <Bell className="mx-auto mb-1.5 h-5 w-5 opacity-60" />
                  No notifications match the filter.
                </li>
              ) : (
                list.map((a) => <NotificationRow key={a.id} alert={a} />)
              )}
            </ul>

            <footer className="flex items-center gap-1 border-t border-border-default bg-surface-2 px-2 py-1.5">
              <button
                type="button"
                onClick={toggleAudio}
                title={audio ? 'Mute audio alerts' : 'Enable audio alerts'}
                className={cn(
                  'grid h-7 w-7 place-items-center rounded text-fg-secondary hover:bg-surface-3 hover:text-fg-primary',
                  audio && 'text-accent',
                )}
              >
                {audio ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={toggleDesktopPush}
                title={desktopPush ? 'Disable OS push' : 'Enable OS push'}
                className={cn(
                  'grid h-7 w-7 place-items-center rounded text-fg-secondary hover:bg-surface-3 hover:text-fg-primary',
                  desktopPush && 'text-accent',
                )}
              >
                {desktopPush ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              </button>
              <Button variant="ghost" size="sm" onClick={markAllRead}>
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </Button>
              <Button variant="ghost" size="sm" onClick={clearAll} className="ml-auto">
                <Archive className="h-3.5 w-3.5" />
                Clear
              </Button>
            </footer>
          </aside>
        </>
      )}
    </div>
  );
}

function NotificationRow({ alert }: { alert: AppAlert }) {
  const markRead = useNotifications((s) => s.markRead);
  const remove = useNotifications((s) => s.remove);
  const sev = SEVERITY_META[alert.severity];
  const source = SOURCE_META[alert.source];
  const Icon = alert.severity === 'critical' ? AlertOctagon : alert.severity === 'warn' ? AlertTriangle : Info;
  return (
    <li
      onClick={() => markRead(alert.id)}
      className={cn(
        'group flex cursor-pointer items-start gap-2 px-3 py-2',
        !alert.read && 'bg-accent/5',
        alert.severity === 'critical' && !alert.read && 'bg-status-critical/8',
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid h-6 w-6 flex-none place-items-center rounded',
          alert.severity === 'critical' && 'bg-status-critical/15 text-status-critical',
          alert.severity === 'warn' && 'bg-status-warn/15 text-status-warn',
          alert.severity === 'info' && 'bg-surface-3 text-fg-secondary',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={cn('truncate text-[12px] font-semibold', !alert.read && 'text-fg-primary')}>
            {alert.title}
          </span>
          <Badge tone={source.tone}>{source.label}</Badge>
          {alert.robot_id && (
            <span className="font-mono text-[9.5px] text-fg-muted">{alert.robot_id}</span>
          )}
        </div>
        <p className="mt-0.5 text-[11px] leading-snug text-fg-secondary">{alert.body}</p>
        <div className="mt-1 flex items-center gap-2 text-[10px] text-fg-muted">
          <span className="font-mono">{fmtAgo(alert.ts)}</span>
          <Badge tone={sev.tone}>{sev.label}</Badge>
          {alert.action && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                markRead(alert.id);
              }}
              className="ml-auto flex items-center gap-1 rounded border border-accent/40 bg-accent/15 px-1.5 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/25"
            >
              <CornerDownLeft className="h-2.5 w-2.5" />
              {alert.action.label}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          remove(alert.id);
        }}
        title="Dismiss"
        className="grid h-5 w-5 flex-none place-items-center rounded text-fg-muted opacity-0 hover:bg-surface-3 hover:text-status-critical group-hover:opacity-100"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </li>
  );
}

function SevPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-6 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium uppercase tracking-wider transition-colors',
        active
          ? 'border-accent bg-accent/15 text-accent'
          : 'border-border-default bg-surface-2 text-fg-secondary hover:bg-surface-3 hover:text-fg-primary',
      )}
    >
      {children}
    </button>
  );
}

function fmtAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 60 * 60_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 24 * 60 * 60_000) return `${Math.floor(diff / (60 * 60_000))} h ago`;
  return `${Math.floor(diff / (24 * 60 * 60_000))} d ago`;
}

function fmtRemain(expiry: number): string {
  const diff = expiry - Date.now();
  if (diff < 60_000) return 'soon';
  if (diff < 60 * 60_000) return `${Math.floor(diff / 60_000)} min`;
  return `${Math.floor(diff / (60 * 60_000))} h`;
}
